import { useState, useEffect, useRef } from 'react';
import { Card, Row, Col } from 'antd';
import Cookies from 'js-cookie';

/**
 * Multilingual labels for project information sections.
 * Maps field names to their translations in English and German.
 * 
 * @type {Object} Translation object for UI labels
 */
const row_name = { 
    description: { en: "Description", de: "Beschreibung" }, 
    date: { en: "Date", de: "Datum" }, 
    technic: { en: "Technologies", de: "Technologien" } 
};

/**
 * ShowProjects component - Displays a filterable list of projects.
 * 
 * @component
 * @param {Array} projectsList - Array of project objects from projects.json
 * @param {Array} selectedTags - Array of selected technology tags for filtering
 * @param {Array} selectedElement - [id, type] array for element selection and scrolling
 * @returns {JSX.Element} Grid of project cards with filtering and navigation
 */
const ShowProjects = ({ projectsList, selectedTags, selectedElement }) => {
    // ==================== STATE MANAGEMENT ====================
    
    /** @type {string} Current language preference from cookies */
    const [language, setLanguage] = useState(Cookies.get('language') || 'de');
    
    /** @type {Array} Filtered list of projects to display based on selected tags */
    const [projects, setProjects] = useState([]);

    // ==================== REFS FOR SCROLLING ====================
    
    /** 
     * Ref object to store DOM references for each project card.
     * Used for smooth scrolling to specific projects when selected from sidebar.
     * Structure: { projectId: domElement, ... }
     */
    const projectRefs = useRef({});

    // ==================== EFFECTS ====================

    /**
     * Language preference monitoring effect.
     * Polls cookie changes every second to detect language preference updates.
     * Ensures component stays synchronized with language changes from other parts of the app.
     */
    useEffect(() => {
        const handleCookieChange = () => {
            const newLanguage = Cookies.get('language') || 'de';
            setLanguage(newLanguage);
        };

        // Poll for language changes every second
        const interval = setInterval(handleCookieChange, 1000);

        return () => clearInterval(interval); // Cleanup on unmount
    }, []);


    /**
     * Projects list synchronization effect.
     * Updates local projects state when the parent component provides new project data.
     */
    useEffect(() => {
        setProjects(projectsList);
    }, [projectsList]);

    /**
     * Tag-based filtering effect.
     * Filters projects based on selected technology tags from the navigation bar.
     */
    useEffect(() => {
        let showProjects = [];
        
        if (selectedTags.length > 0) {
            // Filter projects that match at least one selected tag
            for (let i = 0; i < projectsList.length; i++) {
                const projectTags = projectsList[i]["technic"];
                
                // Check if any technology in this project matches selected tags
                for (let j = 0; j < projectTags.length; j++) {
                    if (selectedTags.includes(projectTags[j]["name"])) {
                        showProjects.push(projectsList[i]);
                        break; // Avoid adding the same project multiple times
                    }
                }
            }
        } else {
            // No filters applied - show all projects
            showProjects = projectsList;
        }
        
        setProjects(showProjects);
    }, [selectedTags, projectsList]); // Fixed dependency: was [selectedTags, selectedTags]

    /**
     * Smooth scrolling effect for project navigation.
     * Automatically scrolls to a specific project when selected from the sidebar.
     */
    useEffect(() => {
        console.log("check element:", selectedElement);
        
        if (
            selectedElement !== null &&
            selectedElement[1] === "Projects" &&
            projectRefs.current[selectedElement[0]]
        ) {
            // Delay to ensure refs are attached after render
            setTimeout(() => {
                projectRefs.current[selectedElement[0]].scrollIntoView({
                    behavior: "smooth",
                    block: "center", // Center the element in the viewport
                });
            }, 100); // 100ms is usually sufficient for DOM updates
        }
    }, [selectedElement, projects]);

    // ==================== RENDER ====================

    return (
        <div style={{ 
            background: 'linear-gradient(to right,rgb(175, 82, 175),rgb(39, 39, 131))' 
        }}>
            <Row gutter={[16, 16]} style={{ padding: '20px' }}>
                {projects && projects.map((project, index) => (
                    <Col
                        key={project.id || index}
                        span={24} // Full width cards for better readability
                        ref={el => {
                            // Store DOM reference for each project by ID
                            if (project.id) projectRefs.current[project.id] = el;
                        }}
                    >
                        <Card
                            title={
                                <div style={{ 
                                    wordWrap: 'break-word', 
                                    whiteSpace: 'normal' 
                                }}>
                                    {project.title[language]}
                                </div>
                            }
                            bordered={true}
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                maxWidth: 700,
                                margin: '24px auto', // Center cards with consistent spacing
                            }}
                        >
                            {/* Project Date Section */}
                            <p style={{ marginTop: '-20px' }}>
                                <strong>{row_name.date[language]}:</strong>
                                <br />
                                {project.date[language]}
                            </p>

                            {/* Project Description Section */}
                            <p>
                                <strong>{row_name.description[language]}:</strong>
                                <br />
                                {project.description[language]}
                            </p>

                            {/* Technologies Section with Color-Coded Badges */}
                            <p>
                                <strong>{row_name.technic[language]}:</strong>
                                <br />
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '10px', 
                                    flexWrap: 'wrap' 
                                }}>
                                    {project.technic.map((tech, techIndex) => (
                                        <div
                                            key={techIndex}
                                            style={{
                                                backgroundColor: tech.color, // Custom color for each technology
                                                color: 'white',
                                                padding: '5px 10px',
                                                borderRadius: '8px',
                                                textAlign: 'center',
                                                minWidth: '80px',
                                            }}
                                        >
                                            {tech.name}
                                        </div>
                                    ))}
                                </div>
                            </p>

                            {/* External Links Section */}
                            {project.links && project.links.length > 0 && (
                            <p>
                                <strong>Links:</strong>
                                <br />
                                {project.links.map(link => (
                                    <a
                                        key={link.url}
                                        href={link.url}
                                        target="_blank" // Open in new tab for better UX
                                        rel="noopener noreferrer" // Security best practice
                                        style={{ display: "block" }}
                                    >
                                        {link.label}
                                    </a>
                                ))}
                            </p>
                            )}
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default ShowProjects;